import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Slot } from 'radix-ui';

import { ActionButton } from '@/shared/ui/action-button';
import type { ConfirmationActionProps } from '../model';

/**
 * `AlertDialogTrigger asChild` hands its click handler, ref and aria attributes
 * to this component as props; they must reach the rendered button, or the
 * dialog never opens.
 */
type TriggerProps = Pick<
  ConfirmationActionProps,
  'trigger' | 'triggerText' | 'triggerIntent' | 'triggerClassName' | 'triggerDisabled' | 'isLoading' | 'loadingText'
> &
  ComponentProps<'button'>;

export function Trigger({
  trigger,
  triggerText,
  triggerIntent = 'primary',
  triggerClassName,
  triggerDisabled = false,

  isLoading = false,
  loadingText: _loadingText,
  ...triggerProps
}: TriggerProps) {
  const { t } = useTranslation();

  if (trigger) {
    return <Slot.Root {...triggerProps}>{trigger}</Slot.Root>;
  }

  return (
    <ActionButton
      {...triggerProps}
      intent={triggerIntent}
      className={triggerClassName}
      disabled={triggerDisabled || isLoading}>
      {triggerText || t('common.action')}
    </ActionButton>
  );
}
